import { shortcutKeys, spokenShortcut } from "@/lib/shortcuts";
import { useShortcutStore } from "@/stores/shortcut-store";
import { CONCEPT_ICONS } from "@/components/ui/concept-icons";
import { useCallback, useEffect, useRef, useState } from "react";
import type { CSSProperties, PointerEvent } from "react";
import type { DurableConversation } from "@/api/conversations";
import { useAgentDockItems } from "@/components/cursors/use-agent-dock-items";
import { useCanvasStore } from "@/stores/canvas-store";
import { useAgentStore } from "@/stores/agent-store";
import { useCompactAgentDisplayStore } from "@/stores/compact-agent-display-store";
import { useNotificationStore } from "@/stores/notification-store";
import { useConversationBrowser } from "@/hooks/use-conversation-browser";
import { openDurableConversationFrame } from "@/lib/open-durable-conversation-frame";
import { CompactRecentChatsCard } from "./compact-denker-chats";
import { CompactProjectSpaceRow } from "./compact-project-space-row";
import { useDesktopMode } from "./use-desktop-mode";
import { useDictation } from "./use-dictation";
import { useDesktopUpdateStore } from "./desktop-update-store";
import { restoreAndFocusCurrentWindow } from "./use-global-hotkey";
import { getWindowDisplayId } from "./use-window-role";
import { openGlobalSearch } from "@/lib/global-search";
import { OPEN_COMPACT_HISTORY_EVENT } from "@/lib/compact-history-event";
import { surfaceRoleAttributes } from "@/components/ui/surface-contract";
import { CompactAgentPanel } from "./compact-agent-panel";
import { CompactDenkerStatusButton } from "./compact-denker-status-logo";
import {
  CompactActionButton,
  CompactActionInfoPanel,
  type CompactActionInfo,
} from "./compact-denker-actions";
import { useCompactAgentAnchor } from "./use-compact-agent-anchor";
import {
  COMPACT_WIDGET_LOGO_CENTER,
  COMPACT_WIDGET_SHELL_WIDTH,
  COMPACT_WIDGET_UPDATE_SHELL_WIDTH,
} from "./compact-widget-geometry";
import { CompactDenkerVoiceStatus } from "./compact-denker-voice-status";
import { CompactUpdateActionButton } from "./compact-update-action-button";
import type { CompactVoiceStatus } from "./use-compact-voice-status";
import { resolveCursorInputAnchor } from "./cursor-input-anchor";
import { useT } from "@/i18n";
import { compactMessages } from "@/i18n/messages/compact";

interface CompactDenkerWidgetProps {
  expanded: boolean;
  expandUp?: boolean;
  panelSpace?: number;
  dragging?: boolean;
  docked?: boolean;
  onRequestCollapse?: (options?: { delayed?: boolean }) => void;
  voice: CompactVoiceStatus;
}
const RECENT_CHAT_LIMIT = 3;
const RECENT_CHAT_ROW_HEIGHT = 48;
const RECENT_CHAT_PANEL_CHROME_HEIGHT = 44;
const AGENT_PANEL_HEIGHT = 240;
// Keep widget summons distinct from the native hotkey's positive counter.
let nextWidgetSummonId = -Date.now();
export function CompactDenkerWidget({
  expanded,
  expandUp = false,
  panelSpace,
  dragging = false,
  docked = true,
  onRequestCollapse,
  voice,
}: CompactDenkerWidgetProps) {
  useShortcutStore((state) => state.overrides);
  const t = useT(compactMessages);
  const rootRef = useRef<HTMLDivElement>(null);
  const logoRef = useRef<HTMLButtonElement>(null);
  const selectChatRequestSeqRef = useRef(0);
  const [actionInfo, setActionInfo] = useState<CompactActionInfo | null>(null);
  const [chatCardOpen, setChatCardOpen] = useState(false);
  const [agentPanelOpen, setAgentPanelOpen] = useState(false);
  const [selectedAgentId, setSelectedAgentId] = useState<string | null>(null);
  const [preferSessions, setPreferSessions] = useState(false);
  const [projectSwitcherOpen, setProjectSwitcherOpen] = useState(false);
  const agentItems = useAgentDockItems({ prioritize: true, denkerOnly: true });
  const dockEnabled = useCompactAgentDisplayStore((state) => state.dockEnabled);
  const highlightedAgentId = useCompactAgentDisplayStore(
    (state) => state.highlightedAgentId,
  );
  // Single Denker streaming view: denkerOnly already excludes native
  // subagents, other agents, and orphaned entries without agent identity —
  // those stream in the right-edge rail and toast cards, never here.
  // The pinned item owns the widget — no per-agent tab switching, no badge.
  const visibleAgentItems = dockEnabled ? agentItems : [];
  const selectedAgentItem =
    visibleAgentItems.find((item) => item.cursor.agentId === selectedAgentId) ??
    visibleAgentItems[0];
  const unseenAgentItem = visibleAgentItems.find(
    (item) => item.hasUnseenResult,
  );
  const logoAgentItem = unseenAgentItem ?? selectedAgentItem;
  useCompactAgentAnchor({
    anchorRef: logoRef,
    agentId: selectedAgentItem?.cursor.agentId ?? null,
    enabled: dockEnabled,
  });
  const { rows: listedChats, refresh: refreshChats } = useConversationBrowser({
    limit: RECENT_CHAT_LIMIT,
  });
  const recentChats = listedChats.slice(0, RECENT_CHAT_LIMIT);
  const addToast = useNotificationStore((s) => s.addSystemToast);
  const selectAgent = useAgentStore((s) => s.selectAgent);
  const readyVersion = useDesktopUpdateStore((s) => s.readyVersion);
  const isInstalling = useDesktopUpdateStore((s) => s.isInstalling);
  const desktopMode = useDesktopMode((s) => s.mode);
  const openWorkspace = useDesktopMode((s) => s.openWorkspace);
  const closeWorkspace = useDesktopMode((s) => s.closeWorkspace);
  const requestSettings = useDesktopMode((s) => s.requestSettings);
  const workspaceOpen = desktopMode === "denk";
  const isOpen = !dragging && (expanded || workspaceOpen);
  const shellStyle = {
    "--denker-shell-width": `${
      readyVersion
        ? COMPACT_WIDGET_UPDATE_SHELL_WIDTH
        : COMPACT_WIDGET_SHELL_WIDTH
    }px`,
    "--denker-logo-center": `${COMPACT_WIDGET_LOGO_CENTER}px`,
  } as CSSProperties;
  const chatMenuOpen = isOpen && chatCardOpen;
  const agentMenuOpen =
    isOpen && agentPanelOpen && visibleAgentItems.length > 0;
  const projectSwitcherPanelOpen =
    isOpen && (projectSwitcherOpen || workspaceOpen);
  const chatMenuHeight =
    Math.max(1, recentChats.length) * RECENT_CHAT_ROW_HEIGHT +
    RECENT_CHAT_PANEL_CHROME_HEIGHT;
  const actionInfoOpen =
    isOpen && actionInfo !== null && !chatMenuOpen && !agentMenuOpen;
  const agentPanelHeight = AGENT_PANEL_HEIGHT;
  const panelHeight = agentMenuOpen
    ? `min(${agentPanelHeight}px, max(0px, var(--denker-panel-space, calc(100dvh - 64px))))`
    : chatMenuOpen
      ? `min(${chatMenuHeight}px, max(0px, var(--denker-panel-space, calc(100dvh - 64px))))`
      : actionInfoOpen
        ? "min(64px, max(0px, var(--denker-panel-space, calc(100dvh - 64px))))"
        : projectSwitcherPanelOpen
          ? "min(44px, max(0px, var(--denker-panel-space, calc(100dvh - 64px))))"
          : "0px";
  useEffect(() => {
    if (expanded) return;
    setActionInfo(null);
    setChatCardOpen(false);
    setAgentPanelOpen(false);
    setSelectedAgentId(null);
    setPreferSessions(false);
    setProjectSwitcherOpen(false);
  }, [expanded]);
  useEffect(() => {
    if (!agentMenuOpen || !selectedAgentItem?.hasUnseenResult) return;
    useCanvasStore
      .getState()
      .acknowledgeAgentCursorResult(selectedAgentItem.cursor.agentId);
  }, [agentMenuOpen, selectedAgentItem]);
  const keepChatCardOpen = useCallback(() => {
    setActionInfo(null);
    setAgentPanelOpen(false);
    setPreferSessions(true);
    setProjectSwitcherOpen(false);
    if (!chatCardOpen) {
      setChatCardOpen(true);
      void refreshChats();
    }
  }, [chatCardOpen, refreshChats]);

  useEffect(() => {
    window.addEventListener(OPEN_COMPACT_HISTORY_EVENT, keepChatCardOpen);
    return () =>
      window.removeEventListener(OPEN_COMPACT_HISTORY_EVENT, keepChatCardOpen);
  }, [keepChatCardOpen]);

  const openLogoPanel = () => {
    if (visibleAgentItems.length === 0 || preferSessions) {
      keepChatCardOpen();
      return;
    }
    const nextSelectedItem =
      visibleAgentItems.find((item) => item.hasUnseenResult) ??
      visibleAgentItems.find(
        (item) => item.cursor.agentId === selectedAgentId,
      ) ??
      visibleAgentItems.find(
        (item) => item.cursor.agentId === highlightedAgentId,
      ) ??
      visibleAgentItems[0];
    if (nextSelectedItem?.hasUnseenResult) {
      useCanvasStore
        .getState()
        .acknowledgeAgentCursorResult(nextSelectedItem.cursor.agentId);
    }
    setActionInfo(null);
    setChatCardOpen(false);
    setProjectSwitcherOpen(false);
    setSelectedAgentId(nextSelectedItem?.cursor.agentId ?? null);
    setAgentPanelOpen(true);
  };

  const closeChatPanel = () => {
    setChatCardOpen(false);
    setAgentPanelOpen(false);
    setProjectSwitcherOpen(false);
  };

  const handleShellPointerLeave = (event: PointerEvent<HTMLDivElement>) => {
    const nextTarget = event.relatedTarget;
    if (nextTarget instanceof Node && rootRef.current?.contains(nextTarget)) {
      return;
    }
    onRequestCollapse?.({ delayed: true });
  };

  const handleChatCardPointerLeave = (event: PointerEvent<HTMLDivElement>) => {
    const nextTarget = event.relatedTarget;
    if (nextTarget instanceof Node && rootRef.current?.contains(nextTarget)) {
      return;
    }
    onRequestCollapse?.({ delayed: true });
  };

  const showActionInfo = (info: CompactActionInfo | null) => {
    setChatCardOpen(false);
    setAgentPanelOpen(false);
    setProjectSwitcherOpen(false);
    setActionInfo(info);
  };

  const startTyping = () => {
    setActionInfo(null);
    setChatCardOpen(false);
    setAgentPanelOpen(false);
    setProjectSwitcherOpen(false);
    useCanvasStore.getState().setPendingCursorSummon({
      ...resolveCursorInputAnchor(rootRef.current?.getBoundingClientRect()),
      summonId: nextWidgetSummonId--,
      displayId: getWindowDisplayId(),
      placement: "anchor",
    });
    onRequestCollapse?.();
    restoreAndFocusCurrentWindow();
  };

  const startVoice = () => {
    setActionInfo(null);
    setChatCardOpen(false);
    setAgentPanelOpen(false);
    setProjectSwitcherOpen(false);
    const dictation = useDictation.getState();
    if (dictation.status === "processing") return;
    onRequestCollapse?.();
    restoreAndFocusCurrentWindow();
    void dictation.toggle();
  };

  const openSettings = () => {
    setActionInfo(null);
    setChatCardOpen(false);
    setAgentPanelOpen(false);
    setProjectSwitcherOpen(false);
    requestSettings({ page: "root" });
    restoreAndFocusCurrentWindow();
  };

  const toggleWorkspaceFromAction = () => {
    setActionInfo(null);
    setChatCardOpen(false);
    setAgentPanelOpen(false);
    if (workspaceOpen) {
      setProjectSwitcherOpen(false);
      onRequestCollapse?.();
      closeWorkspace();
      return;
    }
    setProjectSwitcherOpen(true);
    openWorkspace();
  };

  const openChatFollowUp = (conversation: DurableConversation) => {
    const requestSeq = ++selectChatRequestSeqRef.current;
    const isCurrentRequest = () =>
      requestSeq === selectChatRequestSeqRef.current;
    setActionInfo(null);
    setChatCardOpen(false);
    setAgentPanelOpen(false);
    setProjectSwitcherOpen(false);
    onRequestCollapse?.();
    restoreAndFocusCurrentWindow();
    void openDurableConversationFrame({
      conversation,
      addToast,
      closePopover: () => {
        setChatCardOpen(false);
      },
      selectAgent,
      shouldContinue: isCurrentRequest,
    });
  };

  const openAllChats = () => {
    setActionInfo(null);
    closeChatPanel();
    onRequestCollapse?.();
    openGlobalSearch({ chip: "chats", projectId: null });
  };
  const openShortcuts = () => {
    setActionInfo(null);
    closeChatPanel();
    onRequestCollapse?.();
    requestSettings({ page: "preferences-shortcuts" });
    restoreAndFocusCurrentWindow();
  };
  return (
    <div
      ref={rootRef}
      className="relative h-9 w-9 shrink-0 aspect-square overflow-visible"
      data-interactive="true"
      data-testid="desktop-compact-denker-anchor"
    >
      <CompactDenkerStatusButton
        ref={logoRef}
        item={logoAgentItem ?? null}
        expanded={isOpen}
        menuOpen={chatCardOpen || agentPanelOpen}
        onOpen={openLogoPanel}
        phase={voice.phase}
        voiceActive={voice.active}
        live={voice.live}
      />
      <div
        className="denker-compact-action-row pointer-events-none absolute top-0 z-[2] flex h-9 items-center justify-start gap-1.5 px-2"
        data-state={isOpen ? "open" : "closed"}
        data-testid="desktop-compact-action-row"
        style={shellStyle}
      >
        <div className="denker-compact-action-group denker-compact-action-group-left flex items-center gap-1">
          <CompactActionButton
            icon={CONCEPT_ICONS.chat}
            label={t("typeToDenker")}
            shortcut={shortcutKeys("shortcut-input-bar")}
            tooltipDescription={[
              t("typeHint", { shortcut: spokenShortcut("shortcut-input-bar") }),
            ]}
            testId="desktop-compact-action-type"
            onInfoChange={showActionInfo}
            onClick={startTyping}
          />
          <CompactActionButton
            icon={CONCEPT_ICONS.voice}
            label={t("talkToDenker")}
            shortcut={shortcutKeys("shortcut-voice")}
            tooltipDescription={[
              t("talkHint", { shortcut: spokenShortcut("shortcut-voice") }),
            ]}
            testId="desktop-compact-action-talk"
            onInfoChange={showActionInfo}
            onClick={startVoice}
          />
        </div>
        <span className="h-7 w-7 shrink-0" aria-hidden="true" />
        <div className="denker-compact-action-group denker-compact-action-group-right flex items-center gap-1">
          <CompactActionButton
            icon={CONCEPT_ICONS.workspace}
            label={t("workspace")}
            shortcut={shortcutKeys("shortcut-toggle-workspace")}
            tooltipDescription={[t("workspaceHint")]}
            testId="desktop-compact-action-open-workspace"
            onInfoChange={showActionInfo}
            onClick={toggleWorkspaceFromAction}
            active={workspaceOpen}
            ariaExpanded={projectSwitcherPanelOpen}
            ariaControls="desktop-compact-project-space-row"
          />
          <CompactActionButton
            icon={CONCEPT_ICONS.settings}
            label={t("settings")}
            tooltipDescription={[t("settingsHint")]}
            testId="desktop-compact-action-settings"
            onInfoChange={showActionInfo}
            onClick={openSettings}
          />
          {readyVersion && (
            <CompactUpdateActionButton
              isInstalling={isInstalling}
              isOpen={isOpen}
              onInfoChange={showActionInfo}
              readyVersion={readyVersion}
            />
          )}
        </div>
      </div>
      <div
        className="denker-compact-denker-material denker-compact-denker-shell absolute top-0 flex shrink-0 flex-col overflow-hidden"
        data-expand-direction={expandUp ? "up" : "down"}
        data-fit-inset={docked ? "top" : undefined}
        data-interactive="true"
        data-state={isOpen ? "open" : "closed"}
        data-voice={voice.active ? "active" : "idle"}
        data-voice-label={voice.showLabel ? "visible" : "hidden"}
        data-voice-phase={voice.phase}
        data-voice-status={voice.status}
        data-panel={
          chatMenuOpen ||
          agentMenuOpen ||
          projectSwitcherPanelOpen ||
          actionInfoOpen
            ? "open"
            : "closed"
        }
        data-testid="desktop-compact-denker-widget"
        {...surfaceRoleAttributes("rail", {
          nativeLevel: "island",
          nativeGroup: "compact-denker",
        })}
        onPointerLeave={handleShellPointerLeave}
        style={
          {
            ...shellStyle,
            "--denker-panel-height": panelHeight,
            "--denker-panel-space":
              panelSpace === undefined ? undefined : `${panelSpace}px`,
          } as CSSProperties
        }
      >
        <CompactDenkerVoiceStatus
          active={voice.active}
          commandLabel={voice.commandLabel}
          agentErrorLabel={
            logoAgentItem?.isError
              ? t("agentEncounteredError", { name: logoAgentItem.cursor.name })
              : undefined
          }
          expanded={isOpen}
          live={voice.live}
          phase={voice.phase}
          voiceErrorLabel={voice.errorLabel}
          screenshotIntent={voice.screenshotIntent}
          showLabel={voice.showLabel}
          status={voice.status}
        />
        <div className="h-9 shrink-0" aria-hidden="true" />
        {voice.active && voice.phase === "error" ? (
          <div
            className="flex min-h-9 shrink-0 items-center py-1 pl-9 pr-2 text-appkit-caption font-semibold text-[var(--color-readable-overlay-primary)]"
            data-testid="desktop-compact-voice-error"
            role="status"
          >
            {voice.errorLabel ?? t("voiceUnavailable")}
          </div>
        ) : null}
        {agentMenuOpen && selectedAgentItem ? (
          <CompactAgentPanel
            item={selectedAgentItem}
            onBack={keepChatCardOpen}
            onRequestCollapse={() => {
              closeChatPanel();
              onRequestCollapse?.();
            }}
          />
        ) : chatMenuOpen ? (
          <CompactRecentChatsCard
            chats={recentChats}
            onKeepOpen={keepChatCardOpen}
            onRequestClose={handleChatCardPointerLeave}
            onSelectChat={openChatFollowUp}
            onViewAllChats={openAllChats}
            onShowShortcuts={openShortcuts}
          />
        ) : actionInfoOpen ? (
          <CompactActionInfoPanel info={actionInfo} />
        ) : projectSwitcherPanelOpen ? (
          <CompactProjectSpaceRow />
        ) : null}
      </div>
    </div>
  );
}
